import { Injectable } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { EntityType, IOrganisation } from '@portal/entity-services/interfaces';
import { OrganisationService } from '@portal/entity-services/organisations/src/lib/services/organisation.service';
import { ICount } from '@portal/shared/vui-http/src/lib/interfaces/count.interface';
import { Observable, of } from 'rxjs';
import { map, mergeMap, shareReplay } from 'rxjs/operators';

declare const $localize;

@Injectable()
export class EntityValidators {
  constructor(private organisationService: OrganisationService) {}

  /**
   * Async validator for checking merchant company & merchant company group in hierarchy
   * @param entity - The organisation in whose hierarchy we are looking for a merchant company or merchant company group
   * @param isCreateMode - When isCreateMode true entityUid is id of parent organisation
   */
  asyncValidateMerchantCompany(entity: IOrganisation, isCreateMode?: boolean): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const entityType = control.value;

      if (!entity.entityUid || entityType === entity.entityType) {
        return of(null);
      }
      if (
        entityType !== EntityType.MERCHANT_COMPANY &&
        entityType !== EntityType.MERCHANT_COMPANY_GROUP
      ) {
        return of(null);
      }

      const descendantsCount$ = isCreateMode
        ? of({ count: 0 })
        : this.organisationService
            .getTotalCount({
              entityType: entityType,
              ancestorId: entity.entityUid,
            })
            .pipe(shareReplay(1));

      const ancestors$ = this.organisationService
        .getAncestors(entity.entityUid)
        .pipe(shareReplay(1));

      return descendantsCount$.pipe(
        mergeMap((descendantsCount: ICount) => {
          if (descendantsCount.count !== 0) {
            return of(this.createValidationError(entityType));
          }
          const hasEntity$ = ancestors$.pipe(
            map((ancestors) => {
              return entityType === EntityType.MERCHANT_COMPANY
                ? this.organisationService.getMerchantCompany(ancestors)
                : this.organisationService.getMerchantCompanyGroup(ancestors);
            }),
            map((hasEntity) => !!hasEntity),
          );

          return hasEntity$.pipe(
            map((hasEntity) => (hasEntity ? this.createValidationError(entityType) : null)),
          );
        }),
      );
    };
  }

  asyncValidateParentForMerchantCompany(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors> => {
      return of(control.value).pipe(
        mergeMap((parentEntityId) => {
          if (!parentEntityId) {
            return of(undefined);
          }
          const ancestors$ = this.organisationService
            .getAncestors(parentEntityId)
            .pipe(shareReplay(1));
          return ancestors$.pipe(
            map((ancestors) => this.organisationService.getMerchantCompany(ancestors)),
          );
        }),
        map((merchantEntity) => {
          if (merchantEntity && merchantEntity?.entityType === EntityType.MERCHANT_COMPANY) {
            return {
              parentOrg: {
                message: '@@HAS_MERCHANT_COMPANY',
                displayMessage: $localize`There is already a Merchant Company created in your hierarchy`,
              },
            };
          }
          return undefined;
        }),
      );
    };
  }

  asyncValidateParentForMerchantSite(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors> => {
      return of(control.value).pipe(
        mergeMap((parentEntityId) => {
          if (!parentEntityId) {
            return of(undefined);
          }

          return this.organisationService.getByKey(parentEntityId);
        }),
        map((merchantEntity) => {
          if (merchantEntity && merchantEntity?.entityType !== EntityType.MERCHANT_COMPANY) {
            return {
              parentOrg: {
                message: '@@NOT_MERCHANT_COMPANY',
                displayMessage: $localize`Please select a Merchant company as parent.`,
              },
            };
          }
          return undefined;
        }),
      );
    };
  }

  private createValidationError(entityType: EntityType): ValidationErrors {
    const key =
      entityType === EntityType.MERCHANT_COMPANY ? 'merchantCompany' : 'merchantCompanyGroup';
    const messageKey =
      entityType === EntityType.MERCHANT_COMPANY
        ? '@@HAS_MERCHANT_COMPANY'
        : '@@HAS_MERCHANT_COMPANY_GROUP';
    const displayMessage =
      entityType === EntityType.MERCHANT_COMPANY
        ? $localize`:@@merchantCompanyMessage: There is already a Merchant Company created in your hierarchy`
        : $localize`:@@merchantCompanyGroupMessage: There is already a Merchant Company Group created in your hierarchy`;

    return {
      [key]: {
        message: messageKey,
        displayMessage: displayMessage,
      },
    };
  }
}
